/**
 * Loads + records user corrections for the section classifier.
 *
 * Append-only JSONL on the API. Each record is either an `update` (modify
 * an existing classifier-emitted section by index) or an `insert` (add a
 * brand-new user-defined section). The hook merges everything and exposes:
 *
 *   - `overrides[idx]`: { type?, startTime?, endTime? }      // updates
 *   - `inserts`: array of { insertId, type, startTime, endTime }
 *   - `submitType()`, `submitBoundary()`: update an existing section
 *   - `submitInsert()`, `removeInsert()`: add or undo a user-created section
 */

import { useCallback, useEffect, useState } from 'react';
import type { SectionType } from '../utils/sectionClassifier';
import { getOverrides, recordOverride, clearOverrides, OverrideRecord } from '../lib/api';

export interface OverrideValue {
  type?: SectionType;
  startTime?: number;
  endTime?: number;
}

export interface InsertValue {
  insertId: string;
  type: SectionType;
  startTime: number;
  endTime: number;
}

type OverrideMap = Record<number, OverrideValue>;

function recordToValue(rec: OverrideRecord): OverrideValue {
  return {
    type: rec.correctedType as SectionType | undefined,
    startTime: rec.correctedStartTime,
    endTime: rec.correctedEndTime,
  };
}

function recordToInsert(rec: OverrideRecord): InsertValue | null {
  if (!rec.insertId || !rec.correctedType ||
      typeof rec.correctedStartTime !== 'number' ||
      typeof rec.correctedEndTime !== 'number') return null;
  return {
    insertId: rec.insertId,
    type: rec.correctedType as SectionType,
    startTime: rec.correctedStartTime,
    endTime: rec.correctedEndTime,
  };
}

function makeInsertId(): string {
  return `ins-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function useSectionOverrides(analysisId: string | undefined) {
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [inserts, setInserts] = useState<InsertValue[]>([]);

  useEffect(() => {
    if (!analysisId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getOverrides(analysisId);
        if (cancelled) return;
        const nextUpdates: OverrideMap = {};
        for (const [k, rec] of Object.entries(resp.updates ?? resp.overrides ?? {})) {
          nextUpdates[Number(k)] = recordToValue(rec);
        }
        const nextInserts: InsertValue[] = Object.values(resp.inserts ?? {})
          .map(recordToInsert)
          .filter((v): v is InsertValue => v !== null);
        setOverrides(nextUpdates);
        setInserts(nextInserts);
      } catch (err) {
        console.warn('[overrides] fetch failed:', (err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [analysisId]);

  const submitType = useCallback(
    async (
      sectionIndex: number,
      originalType: SectionType,
      correctedType: SectionType,
    ) => {
      if (!analysisId) return;
      const prev = overrides[sectionIndex];
      setOverrides((m) => ({
        ...m,
        [sectionIndex]: { ...m[sectionIndex], type: correctedType },
      }));
      try {
        await recordOverride({
          analysisId,
          action: 'update',
          sectionIndex,
          originalType,
          correctedType,
        });
      } catch (err) {
        setOverrides((m) => ({ ...m, [sectionIndex]: { ...prev } }));
        throw err;
      }
    },
    [analysisId, overrides],
  );

  const submitBoundary = useCallback(
    async (
      sectionIndex: number,
      originalType: SectionType,
      originalStartTime: number,
      originalEndTime: number,
      correctedStartTime: number | null,
      correctedEndTime: number | null,
    ) => {
      if (!analysisId) return;
      if (correctedStartTime === null && correctedEndTime === null) return;
      const prev = overrides[sectionIndex];
      setOverrides((m) => ({
        ...m,
        [sectionIndex]: {
          ...m[sectionIndex],
          ...(correctedStartTime !== null ? { startTime: correctedStartTime } : {}),
          ...(correctedEndTime !== null ? { endTime: correctedEndTime } : {}),
        },
      }));
      try {
        await recordOverride({
          analysisId,
          action: 'update',
          sectionIndex,
          originalType,
          originalStartTime,
          originalEndTime,
          ...(correctedStartTime !== null ? { correctedStartTime } : {}),
          ...(correctedEndTime !== null ? { correctedEndTime } : {}),
        });
      } catch (err) {
        setOverrides((m) => ({ ...m, [sectionIndex]: { ...prev } }));
        throw err;
      }
    },
    [analysisId, overrides],
  );

  const submitInsert = useCallback(
    async (type: SectionType, startTime: number, endTime: number): Promise<InsertValue | null> => {
      if (!analysisId) return null;
      if (endTime <= startTime) return null;
      const insertId = makeInsertId();
      const value: InsertValue = { insertId, type, startTime, endTime };
      setInserts((arr) => [...arr, value]);
      try {
        await recordOverride({
          analysisId,
          action: 'insert',
          insertId,
          correctedType: type,
          correctedStartTime: startTime,
          correctedEndTime: endTime,
        });
        return value;
      } catch (err) {
        setInserts((arr) => arr.filter((v) => v.insertId !== insertId));
        throw err;
      }
    },
    [analysisId],
  );

  /** Edit a previously-inserted section: type, bounds, or both. */
  const editInsert = useCallback(
    async (
      insertId: string,
      patch: { type?: SectionType; startTime?: number; endTime?: number },
    ) => {
      if (!analysisId) return;
      const prev = inserts.find((v) => v.insertId === insertId);
      if (!prev) return;
      const next: InsertValue = {
        insertId,
        type: patch.type ?? prev.type,
        startTime: patch.startTime ?? prev.startTime,
        endTime: patch.endTime ?? prev.endTime,
      };
      if (next.endTime <= next.startTime) return;
      setInserts((arr) => arr.map((v) => (v.insertId === insertId ? next : v)));
      try {
        await recordOverride({
          analysisId,
          action: 'insert',
          insertId,
          ...(patch.type !== undefined ? { correctedType: patch.type } : {}),
          ...(patch.startTime !== undefined ? { correctedStartTime: patch.startTime } : {}),
          ...(patch.endTime !== undefined ? { correctedEndTime: patch.endTime } : {}),
        });
      } catch (err) {
        setInserts((arr) => arr.map((v) => (v.insertId === insertId ? prev : v)));
        throw err;
      }
    },
    [analysisId, inserts],
  );

  /** Tombstone an inserted section so it disappears from the merged result. */
  const removeInsert = useCallback(
    async (insertId: string) => {
      if (!analysisId) return;
      const prev = inserts.find((v) => v.insertId === insertId);
      if (!prev) return;
      setInserts((arr) => arr.filter((v) => v.insertId !== insertId));
      try {
        await recordOverride({
          analysisId,
          action: 'insert',
          insertId,
          deleted: true,
        });
      } catch (err) {
        setInserts((arr) => [...arr, prev]);
        throw err;
      }
    },
    [analysisId, inserts],
  );

  /** Wipe every recorded correction for this analysis. Optimistic + rollback. */
  const clearAll = useCallback(async () => {
    if (!analysisId) return;
    const prevOverrides = overrides;
    const prevInserts = inserts;
    setOverrides({});
    setInserts([]);
    try {
      await clearOverrides(analysisId);
    } catch (err) {
      setOverrides(prevOverrides);
      setInserts(prevInserts);
      throw err;
    }
  }, [analysisId, overrides, inserts]);

  return {
    overrides,
    inserts,
    submitType,
    submitBoundary,
    submitInsert,
    editInsert,
    removeInsert,
    clearAll,
  };
}
